import { CookieService } from "@Front/Services/CookieService/CookieService";
import {
	LECOFFRE_THIRDPARTY_ACCESS_TOKEN_COOKIE_NAME,
	LECOFFRE_THIRDPARTY_REFRESH_TOKEN_COOKIE_NAME,
} from "@Front/Utils/lecoffreSessionCookieHelpers";
import { setActiveSessionFamily } from "@Front/Utils/lecoffreActiveSessionFamily";
import { EventEmitter } from "@Front/Services/EventEmitter";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";

class ThirdPartyStore {
	private static _instance: ThirdPartyStore | null = null;

	public static get instance(): ThirdPartyStore {
		if (!ThirdPartyStore._instance) {
			ThirdPartyStore._instance = new ThirdPartyStore();
		}
		return ThirdPartyStore._instance;
	}

	private _event: EventEmitter | null = null;

	protected get event(): EventEmitter {
		if (!this._event) {
			this._event = new EventEmitter();
		}
		return this._event;
	}

	private accessToken: string | null = null;

	public isConnected(): boolean {
		return !!this.accessToken;
	}

	public getAccessToken(): string | null {
		return this.accessToken;
	}

	public async connect(accessToken: string, refreshToken: string): Promise<boolean> {
		try {
			CookieService.getInstance().setCookie(LECOFFRE_THIRDPARTY_ACCESS_TOKEN_COOKIE_NAME, accessToken);
			CookieService.getInstance().setCookie(LECOFFRE_THIRDPARTY_REFRESH_TOKEN_COOKIE_NAME, refreshToken);
			this.accessToken = accessToken;
			setActiveSessionFamily("thirdparty");
			this.event.emit("connection", accessToken);
			LoggerService.info("✅ [ThirdPartyStore] Third party connected");
			return true;
		} catch (error: unknown) {
			LoggerService.error("❌ [ThirdPartyStore] Failed to persist tokens", { error });
			return false;
		}
	}

	public async disconnect(): Promise<void> {
		try {
			CookieService.getInstance().deleteCookie(LECOFFRE_THIRDPARTY_ACCESS_TOKEN_COOKIE_NAME);
			CookieService.getInstance().deleteCookie(LECOFFRE_THIRDPARTY_REFRESH_TOKEN_COOKIE_NAME);
			const previousAccessToken = this.accessToken;
			this.accessToken = null;
			this.event.emit("disconnection", previousAccessToken ?? "");
			LoggerService.info("✅ [ThirdPartyStore] Third party disconnected");
		} catch (error: unknown) {
			LoggerService.error("❌ [ThirdPartyStore] Failed to remove third-party tokens", { error });
		}
	}

	public onDisconnect(callback: (userAddress: string) => void): () => void {
		this.event.on("disconnection", callback);
		return () => this.event.off("disconnection", callback);
	}

	public onConnect(callback: (userAddress: string) => void): () => void {
		this.event.on("connection", callback);
		return () => this.event.off("connection", callback);
	}
}

export { ThirdPartyStore };
